import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../lib/config';
import api from '../../lib/api';
import { REPORT } from '../print/PrintAtoms';

interface ElevationCrossSectionProps {
  parcelId: string;
  shareToken?: string;
  // Overrides the "range" prefix on the readout. The profile samples ONE transect along
  // the rep parcel's longest axis, so for assemblies it's a representative transect, not
  // the full elevation range — callers can label it accordingly.
  rangeLabel?: string;
}

interface Sample { distance_ft: number; elevation_ft: number; }

interface ProfileResponse {
  available: boolean;
  reason?: string;
  samples?: Sample[];
  length_ft?: number;
  length_mi?: number;
}

export const ElevationCrossSection: React.FC<ElevationCrossSectionProps> = ({
  parcelId, shareToken, rangeLabel,
}) => {
  const [state, setState] = useState<
    { status: 'loading' } |
    { status: 'ready'; samples: Sample[]; length_mi: number } |
    { status: 'unavailable'; reason: string }
  >({ status: 'loading' });

  useEffect(() => {
    const ctrl = new AbortController();

    const handleResponse = (data: ProfileResponse) => {
      if (data.available && Array.isArray(data.samples) && data.samples.length > 0 && data.length_mi != null) {
        setState({ status: 'ready', samples: data.samples, length_mi: data.length_mi });
      } else {
        setState({ status: 'unavailable', reason: data.reason || '3DEP coverage unavailable' });
      }
    };

    if (shareToken) {
      const url = `${getApiUrl()}/parcels/${parcelId}/terrain-profile?token=${encodeURIComponent(shareToken)}`;
      axios.get<ProfileResponse>(url, { signal: ctrl.signal })
        .then(res => handleResponse(res.data))
        .catch(err => {
          if (err?.name === 'CanceledError' || axios.isCancel?.(err)) return;
          setState({ status: 'unavailable', reason: '3DEP coverage unavailable' });
        });
    } else {
      api.get<ProfileResponse>(`/parcels/${parcelId}/terrain-profile`, { signal: ctrl.signal })
        .then(res => handleResponse(res.data))
        .catch(err => {
          if (err?.name === 'CanceledError' || axios.isCancel?.(err)) return;
          setState({ status: 'unavailable', reason: '3DEP coverage unavailable' });
        });
    }

    return () => ctrl.abort();
  }, [parcelId, shareToken]);

  if (state.status === 'loading') {
    return <div style={{ fontSize: 10, color: REPORT.tertiary, padding: '6px 0' }}>Loading USGS 3DEP cross-section…</div>;
  }
  if (state.status === 'unavailable') {
    // When the caller passes a rangeLabel (Crown Pine A1), the terrain stats above are
    // from the offline native-1m analysis — a "3DEP coverage unavailable" line right
    // under them reads as a contradiction. Render nothing instead of a caveat.
    if (rangeLabel) return null;
    return (
      <div style={{ fontSize: 10, color: REPORT.tertiary, padding: '6px 0' }}>
        Elevation cross-section unavailable ({state.reason}). Slope histogram above is measured.
      </div>
    );
  }

  const { samples, length_mi } = state;
  const WIDTH = 500;
  const HEIGHT = 90;
  const PADDING = { top: 14, bottom: 22, left: 44, right: 12 };
  const plotW = WIDTH - PADDING.left - PADDING.right;
  const plotH = HEIGHT - PADDING.top - PADDING.bottom;

  const elevations = samples.map(s => s.elevation_ft);
  const minEl = Math.min(...elevations);
  const maxEl = Math.max(...elevations);
  const elRange = Math.max(1, maxEl - minEl);
  const maxDist = samples[samples.length - 1].distance_ft || 1;

  const pts = samples.map(s => {
    const x = PADDING.left + (s.distance_ft / maxDist) * plotW;
    const y = PADDING.top + plotH - ((s.elevation_ft - minEl) / elRange) * plotH;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const linePath = `M ${pts.join(' L ')}`;
  const firstX = PADDING.left;
  const lastX = PADDING.left + plotW;
  const bottomY = PADDING.top + plotH;
  const fillPath = `${linePath} L ${lastX},${bottomY} L ${firstX},${bottomY} Z`;

  return (
    <div style={{ marginBottom: 8 }}>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%" style={{ display: 'block' }}
           preserveAspectRatio="xMidYMid meet">
        <line x1={PADDING.left} y1={PADDING.top} x2={PADDING.left} y2={bottomY}
              stroke={REPORT.rule} strokeWidth={0.5} />
        <line x1={PADDING.left} y1={bottomY} x2={lastX} y2={bottomY}
              stroke={REPORT.rule} strokeWidth={0.5} />

        <path d={fillPath} fill={REPORT.accent} fillOpacity={0.08} />
        <path d={linePath} fill="none" stroke={REPORT.accent} strokeWidth={1.5} strokeLinejoin="round" />

        <text x={PADDING.left - 4} y={PADDING.top + 4}
              fill={REPORT.tertiary} fontSize={7} textAnchor="end">
          {Math.round(maxEl).toLocaleString()} ft
        </text>
        <text x={PADDING.left - 4} y={bottomY}
              fill={REPORT.tertiary} fontSize={7} textAnchor="end">
          {Math.round(minEl).toLocaleString()} ft
        </text>

        <text x={PADDING.left + plotW / 2} y={HEIGHT - 4}
              fill={REPORT.tertiary} fontSize={7.5} textAnchor="middle">
          {length_mi.toFixed(2)} mi along parcel's longest axis
        </text>

        <text x={lastX} y={PADDING.top + 4}
              fill={REPORT.secondary} fontSize={7.5} textAnchor="end" fontWeight={600}>
          {rangeLabel || 'range'} {Math.round(maxEl - minEl).toLocaleString()} ft · {samples.length} USGS 3DEP samples
        </text>
      </svg>
    </div>
  );
};
